import React, { useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { PDFDocument } from 'pdf-lib';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import api from '../../services/api';
import { AuthContext } from '../../contexts/AuthContext';
import AppShell from '../../components/layout/AppShell';
import { Button, PageHeader, Input, Alert, Dropzone, FileRow } from '../../components/ui';

function AddMonthlyReport() {
  const { token, role } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const existingReport = location.state?.report || null;

  const [formData, setFormData] = useState({
    clientName: '',
    date: '',
    pdfFiles: [],
  });

  // Compress PDF Before Uploading
  const compressPDF = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);

      // Reduce the quality of images inside the PDF
      pdfDoc.setTitle(file.name);
      pdfDoc.setCreator('PDF Compressor');

      // Convert to Uint8Array
      const compressedPdfBytes = await pdfDoc.save();
      return new File([compressedPdfBytes], file.name, { type: 'application/pdf' });
    } catch (error) {
      console.error('Error compressing PDF:', error);
      return file; // Return original file if compression fails
    }
  };

  // Handle File Drop with Compression
  const onDrop = async (acceptedFiles) => {
    const compressedFiles = await Promise.all(
      acceptedFiles.map(async (file) => {
        if (file.type === 'application/pdf') {
          return await compressPDF(file);
        }
        return file;
      })
    );

    setFormData((prevFormData) => ({
      ...prevFormData,
      pdfFiles: [...prevFormData.pdfFiles, ...compressedFiles],
    }));
  };

  const dropzone = useDropzone({ onDrop, accept: '.pdf' });

  if (role !== 'admin') {
    return (
      <AppShell>
        <Alert
          variant="error"
          title="Access denied"
          action={
            <Button variant="secondary" size="sm" onClick={() => navigate('/')}>
              Back to home
            </Button>
          }
        >
          You do not have permission to access monthly reports. Please contact an administrator.
        </Alert>
      </AppShell>
    );
  }

  // Handle Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);

    const formDataToSend = new FormData();
    formDataToSend.append('clientName', formData.clientName);
    formDataToSend.append('date', formData.date);

    formData.pdfFiles.forEach((file) => {
      formDataToSend.append('pdfFiles', file);
    });

    const url = existingReport
      ? `/monthly-report/monthly-reports/${existingReport._id}`
      : '/monthly-report';

    const method = existingReport ? 'put' : 'post';

    try {
      await api[method](url, formDataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      setSuccessMessage(
        existingReport 
        ? 'Monthly report updated successfully!' 
        : 'Monthly report added successfully!'
      );
      setErrorMessage('');
      setIsSubmitted(true);
      if (existingReport) {
        navigate(`/view-shift-report/${existingReport._id}`);
      }
    } catch (error) {
      setErrorMessage('Error submitting the form. Please try again.');
      setSuccessMessage('');
      console.error(error);
    } finally {
      setIsSubmitting(false); 
    }
  };

  return (
    <>
      <PageHeader
        title={existingReport ? 'Update monthly report' : 'Add monthly report'}
        subtitle="Attach the signed monthly report documents"
        backTo="/viewReports"
      />

      <AppShell>
        <form onSubmit={handleSubmit} className="mx-auto w-full max-w-2xl">
          <div className="rounded-lg border border-border bg-surface p-4 shadow-sm sm:p-6">
            <div className="space-y-4">
              <Input
                label="Client name"
                type="text"
                name="clientName"
                value={formData.clientName}
                onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                required
              />

              <Input
                label="Date"
                type="date"
                name="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />

              <Dropzone label="Report PDFs" hint="PDF documents only" dropzone={dropzone}>
                {formData.pdfFiles.length > 0 && (
                  <ul className="mt-3 space-y-2">
                    {formData.pdfFiles.map((file, index) => (
                      <FileRow
                        key={index}
                        icon={PictureAsPdfOutlined}
                        name={file.name || file}
                        onRemove={() =>
                          setFormData((prev) => ({
                            ...prev,
                            pdfFiles: prev.pdfFiles.filter((_, i) => i !== index),
                          }))
                        }
                      />
                    ))}
                  </ul>
                )}
              </Dropzone>
            </div>

            {errorMessage && (
              <Alert variant="error" className="mt-5">
                {errorMessage}
              </Alert>
            )}
            {successMessage && (
              <Alert variant="success" className="mt-5">
                {successMessage}
              </Alert>
            )}

            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button type="button" variant="secondary" size="lg" onClick={() => navigate('/viewReports')}>
                Cancel
              </Button>
              <Button type="submit" size="lg" loading={isSubmitting} disabled={isSubmitted}>
                {isSubmitted ? 'Submitted' : existingReport ? 'Update report' : 'Submit report'}
              </Button>
            </div>
          </div>
        </form>
      </AppShell>
    </>
  );
}

export default AddMonthlyReport;